import { MailTwoTone } from "@ant-design/icons";
import { assertNever } from "@p0security/shared/types/index";
import {
  RequestNotificationKey,
  RequestNotificationValue,
  RequestNotifications,
} from "@p0security/shared/types/permission";
import { Flex } from "@p0security/ui";
import { SlackLogo } from "components/Integrations/Slack/Slack";
import { TeamsLogo } from "components/Integrations/Teams/TeamsLogo";
import { compact } from "lodash";
import React from "react";
import { spacing } from "styles/spacing";

import { VALID_MESSAGE_TLD } from "../requestUtils";

/** The notifiers this component can render a logo for, in display order. */
const NOTIFIER_KEYS: RequestNotificationKey[] = ["slack", "msTeams", "email"];

/** The URL to link a notifier's logo to, or `undefined` if it has none
 * (e.g. email, which renders a bare logo). */
const notifierConversationUrl = (
  value: RequestNotificationValue
): string | undefined =>
  value && "approvalConversationUrl" in value
    ? value.approvalConversationUrl
    : undefined;

/** Whether a notifier sent anything worth surfacing a logo for. */
const isNotifierConfigured = (value: RequestNotificationValue): boolean =>
  !!value &&
  (!!notifierConversationUrl(value) ||
    ("approvalEmailData" in value && !!value.approvalEmailData));

export const NotifierConversationLinks: React.FC<{
  notifications?: RequestNotifications;
  size?: number;
}> = ({ notifications, size }) => {
  return (
    <Flex gap={spacing.scale.xs} align="center">
      {compact(
        NOTIFIER_KEYS.map((notifier) => {
          const value = notifications?.[notifier];
          if (!isNotifierConfigured(value)) return null;

          const url = notifierConversationUrl(value);
          return url ? (
            <ConversationLink
              key={notifier}
              url={url}
              notifier={notifier}
              size={size}
            />
          ) : (
            <NotifierLogo key={notifier} notifier={notifier} size={size} />
          );
        })
      )}
    </Flex>
  );
};

const ConversationLink: React.FC<{
  url: string;
  notifier: RequestNotificationKey;
  size?: number;
}> = ({ url, notifier, size }) => {
  // Prevent most XSS with this sanitizer; note that this is theoretically redundant
  // as our backend should only write the link with trusted data from Slack anyway
  const hostname = new URL(url).hostname;
  const isValid =
    url && VALID_MESSAGE_TLD.some((domain) => hostname.endsWith(domain));
  return (
    <>
      {isValid && (
        <a href={url} target="_blank" rel="noopener noreferrer">
          <NotifierLogo notifier={notifier} size={size} />
        </a>
      )}
    </>
  );
};

const NotifierLogo: React.FC<{
  notifier: RequestNotificationKey;
  size?: number;
}> = ({ notifier, size = spacing.scale.lg }) => {
  switch (notifier) {
    case "slack":
      return <SlackLogo size={size} />;
    case "msTeams":
      return <TeamsLogo size={size} />;
    case "email":
      return <MailTwoTone size={size} />;
    default:
      assertNever(notifier);
      return <span />;
  }
};
